import { Alert } from '@mui/material';
import * as React from 'react';
import { useCallback, useEffect, useMemo } from 'react';
import { RouteComponentProps, generatePath, useRouteMatch } from 'react-router';
import { Redirect, Route, Switch } from 'react-router-dom';

import { useHasChanged } from '~/hooks/useHasChanged';
import { useEmployeeModuleTabContext } from '~/scenes/PortalViews/Views/EmployeeViews/Modules/components/EmployeeModuleTabs/context';
import { useSubTabPath } from '~/scenes/PortalViews/Views/EmployeeViews/Modules/components/EmployeeModuleTabs/useSubTabPath';

import { getDefaultTabPath } from './data';

type RenderProps<TabPath> = RouteComponentProps & {
  enabledTabPaths: TabPath[];
  modulePath: string;
  tab: string;
};

type PropsType<TabPath> = {
  defaultTab: TabPath;
  enabledTabPaths: TabPath[];
  render: (props: RenderProps<TabPath>) => React.ReactNode;
};

export const EmployeeModuleTabsRouter = <TabPath,>(
  props: PropsType<TabPath>,
) => {
  const { enabledTabPaths, defaultTab, render } = props;

  const { path, params } = useRouteMatch();
  const rootPath = useMemo(() => generatePath(path, params), [path, params]);

  // Keep active tab in sync with route
  const { activeTab, setActiveTab } = useEmployeeModuleTabContext({
    enabledTabPaths,
    defaultTab,
  });
  const { routeTab } = useSubTabPath({ enabledTabPaths, defaultTab });
  const tabRouteChanged = useHasChanged(routeTab);

  useEffect(() => {
    if (
      !!routeTab &&
      activeTab !== routeTab &&
      (tabRouteChanged || !activeTab)
    ) {
      setActiveTab(routeTab);
    }
  }, [activeTab, routeTab, tabRouteChanged, setActiveTab]);

  // Resolve paths
  const { hasEnabledTabs, defaultPath } = useMemo(() => {
    const defaultTabPath = getDefaultTabPath(enabledTabPaths, defaultTab);
    const defaultPath = !!defaultTabPath
      ? `${rootPath}/${defaultTabPath}`
      : rootPath;

    const hasEnabledTabs = enabledTabPaths.length > 0;

    return {
      defaultPath,
      defaultTabPath,
      hasEnabledTabs,
    };
  }, [rootPath, enabledTabPaths, defaultTab]);

  const isTabEnabled = useCallback(
    (tab: string) => {
      return enabledTabPaths.map((t) => `${t}`).includes(tab);
    },
    [enabledTabPaths],
  );

  if (!hasEnabledTabs) {
    return <Alert severity={'info'}>No tabs are enabled for this module</Alert>;
  }

  return (
    <Switch>
      <Route
        path={`${path}/:tab`}
        render={(renderProps) => {
          const { tab } = renderProps.match.params;

          if (!tab || !isTabEnabled(tab)) {
            return <Redirect to={defaultPath} />;
          }

          return render({
            ...renderProps,
            enabledTabPaths,
            modulePath: rootPath,
            tab,
          });
        }}
      />
      <Route exact path={path} render={() => <Redirect to={defaultPath} />} />
    </Switch>
  );
};
